import styled from "styled-components";
import {radii} from "theme/tokens";

/**
 * Styles for {@link ./BrandSettings}, the branding editor (api#98). Externalized per the styling
 * mandate. Renamed to follow its only remaining consumer when ui#495 removed the account-page card.
 *
 * The one rule that is behaviour, not taste: the sender's accent is only ever a BACKGROUND, and wherever
 * TEXT sits on it that text is `--brand-on`, the foreground the api derived for it. Painting the accent as
 * text or as a border on the page background would have an uncontrolled contrast ratio and is exactly how
 * a recipient page becomes unreadable.
 *
 * The accent is painted in two places since ui#622: {@link PreviewBar} here, and the dots plus centre chip
 * of the radial picker (`BrandColorPalette.styles.ts`). The picker carries no text on any accent, so the
 * derived foreground has nothing to do there — the rule above is about text on accent, not about the
 * number of surfaces, which is why a second one is fine.
 */

export const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
`;

export const FieldLabel = styled.span`
	font-size: 13px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const Hint = styled.span`
	font-size: 12.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The saved logo, boxed so a wide or tall image cannot push the card around. */
export const LogoFrame = styled.div`
	width: 96px;
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	overflow: hidden;

	img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
`;

export const HiddenFileInput = styled.input`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

export const ColorValue = styled.code`
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink2};
	text-transform: lowercase;
`;

/**
 * The recipient preview. The accent pair arrives as inline custom properties so a sender-chosen colour
 * never has to become a theme token.
 */
export const PreviewBar = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px 14px;
	border-radius: ${radii.control};
	background: var(--brand, ${({theme}) => theme.tokens.field});
	color: var(--brand-on, ${({theme}) => theme.tokens.ink});
	font-weight: 700;
	font-size: 14px;

	img {
		max-height: 24px;
		max-width: 96px;
		object-fit: contain;
	}
`;

/**
 * Replaces the whole editor when the plan does not include branding, so nothing is interactive. Exactly
 * ONE of these renders per sheet, whichever section {@link ./brandSheetUpsell} names.
 */
export const LockedNote = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink2};
`;
